import type { CallsClient } from '@wtfalch/calls';
import type { CallEndpoint, Participant } from '@wtfalch/calls/browser';
import type { StageTile } from '@wtfalch/design';
import { Button, CallControls, CallStage, CallTile, Icon } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';

import { CallHeader } from './CallHeader.tsx';
import { CallSidePanel } from './CallSidePanel.tsx';
import { DevicePicker } from './DevicePicker.tsx';
import { useGuestLink } from './guestLink.ts';
import { guestDisplayName } from './guestName.ts';
import { Lobby } from './Lobby.tsx';
import { describeCallError, leaveCall } from './useCallEndpoint.ts';
import type { LobbyState } from './useLobby.ts';

/**
 * The focused view of a call: one `CallTile` per `Participant`, arranged by
 * `@wtfalch/design`'s own `CallStage` -- the "grid" half of agora#251's "a
 * bar, and a grid when focused." Each tile's video is drawn through the
 * endpoint's own `attach()`, which maps a participant id to the roster's
 * identity before reaching the adapter; mic is the endpoint's
 * `setMicrophoneEnabled`, camera is `setMode('audio' | 'video')` -- the
 * SDK's own name for the live camera toggle, distinct from the `mode` given
 * to `join()` once at connect time. Screen share is `publish('screen')` to
 * start and `stopShare()` to stop (agora#412) -- its own pair, not a mode,
 * since starting and stopping a share is not something the other two do.
 * All three are local capture decisions, no renegotiation and no new
 * lease, so there is nothing here for `useCallEndpoint`'s heavier join
 * machinery to do. A refused or cancelled picker rejects `publish()`, which
 * surfaces through the same inline error every other toggle here uses,
 * never a crash. Leaving calls both
 * halves of `leaveCall` -- see its docblock in `useCallEndpoint.ts`.
 * `leaving` guards a second press while the first is still in flight, the
 * same reason `CallDock` carries it.
 */
export function CallGrid({
  endpoint,
  client,
  sessionId,
  participants,
  joining = false,
  onLeft,
  joinError,
  heading,
  startedAt,
  member = false,
  lobby,
  onMinimize,
  onBook,
  panelOpenAtStart = false,
  className,
}: {
  endpoint: CallEndpoint;
  /** The member's calls client. Absent for a guest, who has no control
   *  rights: Leave then ends the media alone. */
  client?: CallsClient;
  sessionId: string;
  /** Who is in the call right now -- `useCallEndpoint`'s own `stats.participants`. */
  participants: Participant[];
  /** The join has not finished: no roster yet, so the stage waits and the
   *  controls are held. */
  joining?: boolean;
  /** Called once media teardown has finished -- see `leaveCall`'s docblock. */
  onLeft?: () => void;
  /** `useCallEndpoint`'s own `error` -- whatever `join()` rejected with. */
  joinError?: unknown;
  /** The header's first line ("Call in #nordics"). Absent, there is no header. */
  heading?: string;
  /** When the call began, for the header's clock. */
  startedAt?: number;
  /** A member of the company, not a guest: Invite and the side panel's
   *  member sections are theirs. */
  member?: boolean;
  /** The guests waiting at the door. Members only. */
  lobby?: LobbyState | null;
  /** Back to the docked bar and the room. */
  onMinimize?: () => void;
  /** Opens a new calendar event for the follow-up. */
  onBook?: () => void;
  /** Start with the side panel open. */
  panelOpenAtStart?: boolean;
  className?: string;
}) {
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(panelOpenAtStart);
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const waitingCount = lobby?.waiting.length ?? 0;
  const [mountedAt] = useState(() => Date.now());
  const guestLink = useGuestLink(client, sessionId);

  const invite = () => {
    setInviteNote(null);
    void guestLink.create().then(async (made) => {
      if (!made) return;
      try {
        await navigator.clipboard.writeText(made.url);
        setInviteNote('Guest link copied.');
      } catch {
        setPanelOpen(true);
        setInviteNote('Copy the guest link from the panel.');
      }
    });
  };

  const displayError =
    error ?? (joinError != null ? describeCallError(joinError, 'Could not join the call.') : null);

  const self = participants.find((person) => person.self);
  const muted = self?.media ? !self.media.microphone : false;
  const cameraOff = self?.media ? !self.media.camera : false;
  const sharing = self?.media?.screen ?? false;
  const sharer = participants.find((person) => person.media?.screen);

  const tiles: StageTile[] = participants.map((person) => {
    const media = person.media;
    const sharing = media?.screen ?? false;
    const hasFeed = sharing || (media?.camera ?? false);
    const name = guestDisplayName(person.displayName, 'Someone');
    return {
      id: person.participantId,
      node: (
        <CallTile
          name={name}
          {...(person.self ? { label: `${name} (you)` } : {})}
          address={person.participantId}
          micOff={media ? !media.microphone : undefined}
          sharing={sharing}
          speaking={media?.speaking}
          video={
            hasFeed ? (
              <ParticipantVideo
                endpoint={endpoint}
                participantId={person.participantId}
                source={sharing ? 'screen' : 'camera'}
                muted={person.self}
              />
            ) : undefined
          }
        />
      ),
    };
  });

  const leave = () => {
    if (leaving) return;
    setLeaving(true);
    setError(null);
    // Two-argument `.then` rather than `.then(onLeft).catch(...)`: a
    // throwing `onLeft` must not land in the rejection handler below and
    // show "Could not leave the call" after the user has already left.
    const left = client
      ? leaveCall(endpoint, client, sessionId, () => {
          setError('Left the call, but the room may still be showing you as on it.');
        })
      : endpoint.leave();
    void left.then(
      () => {
        // `onLeft` is the caller's, and a throw from it must not land in
        // the rejection handler below and show "Could not leave the call"
        // after the user has already left -- it did, this just failed to
        // tell the caller.
        try {
          onLeft?.();
        } catch (onLeftError) {
          console.error('CallGrid: onLeft threw', onLeftError);
        }
      },
      () => {
        setLeaving(false);
        setError('Could not leave the call. Try again.');
      },
    );
  };

  const stage = (
    <div className="flex flex-col flex-1 min-h-0 min-w-0">
      <div className="flex flex-col flex-1 min-h-0 surface-panel-2 p-4">
        {joining ? (
          <div
            className="flex flex-1 items-center justify-center gap-2 text-sm text-muted"
            role="status"
          >
            <Icon name="spinner" size="md" /> Joining the call…
          </div>
        ) : (
          <>
            {sharer && (
              <p className="flex-none m-0 pb-3 text-sm" role="status">
                {sharer.self
                  ? 'You are sharing your screen'
                  : `${guestDisplayName(sharer.displayName, 'Someone')} is sharing their screen`}
              </p>
            )}
            <CallStage tiles={tiles} featured={sharer?.participantId} className="flex-1 min-h-0" />
          </>
        )}
      </div>
      <fieldset disabled={joining} className="m-0 min-w-0 border-0 border-t border-border p-4">
        <CallControls
          labelled
          muted={muted}
          onToggleMute={() => {
            endpoint.setMicrophoneEnabled(muted).catch(() => {
              setError('Could not change the microphone. Try again.');
            });
          }}
          cameraOff={cameraOff}
          onToggleCamera={() => {
            endpoint.setMode(cameraOff ? 'video' : 'audio').catch(() => {
              setError('Could not change the camera. Try again.');
            });
          }}
          sharing={sharing}
          onToggleShare={() => {
            setError(null);
            (sharing ? endpoint.stopShare() : endpoint.publish('screen')).catch(() => {
              setError('Could not change screen sharing. Try again.');
            });
          }}
          onLeave={leave}
          end={
            <>
              {member && (
                <Button
                  size="lg"
                  iconOnly
                  aria-label="Who is here"
                  aria-pressed={panelOpen}
                  onPress={() => {
                    setPanelOpen((open) => !open);
                    lobby?.dismissNotice();
                  }}
                >
                  <Icon name="people" size="md" />
                  {waitingCount > 0 && <span className="unread text-sm">{waitingCount}</span>}
                </Button>
              )}
              <DevicePicker endpoint={endpoint} onError={setError} />
            </>
          }
        />
      </fieldset>
      {displayError && (
        <p className="px-4 pb-3 text-xs text-bad" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );

  return (
    <div className={['flex flex-col min-h-0 h-full', className].filter(Boolean).join(' ')}>
      {heading !== undefined && (
        <CallHeader
          heading={heading}
          count={participants.length}
          startedAt={startedAt ?? mountedAt}
          {...(member
            ? {
                onInvite: invite,
                inviteBusy: guestLink.busy,
                inviteNote: guestLink.error ?? lobby?.notice ?? inviteNote,
              }
            : {})}
          {...(onMinimize ? { onMinimize } : {})}
        />
      )}
      <div className="flex flex-1 min-h-0">
        {stage}
        {member && panelOpen && (
          <CallSidePanel
            participants={participants}
            {...(onBook ? { onBook } : {})}
            onClose={() => setPanelOpen(false)}
          >
            {lobby && <Lobby lobby={lobby} />}
            {guestLink.link && (
              <section aria-label="The guest link" className="flex flex-col gap-2">
                <h3 className="h3 m-0">The link that got them here</h3>
                <span className="text-xs text-muted break-all">{guestLink.link.url}</span>
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    kind="danger"
                    disabled={guestLink.busy}
                    onPress={() => void guestLink.revoke()}
                  >
                    Revoke
                  </Button>
                </span>
                <span className="text-xs text-muted">
                  Revoking it does not remove anyone already inside.
                </span>
              </section>
            )}
          </CallSidePanel>
        )}
      </div>
    </div>
  );
}

/**
 * Draws one participant's camera or screen track into a `<video>` through
 * the endpoint's own `attach()` -- the detach function it returns is this
 * effect's cleanup, the same lifecycle `Stream.tsx`'s own `AttachmentBody`
 * already uses for a media resource that outlives one render. Muted for the
 * local participant only, so this browser never plays its own microphone
 * back to itself. `attach()` throws rather than returning nothing when the
 * adapter cannot draw video, or when a participant the current roster has
 * already dropped is attached mid-race -- `CallTile`'s own Identity disc is
 * what shows instead either way.
 */
function ParticipantVideo({
  endpoint,
  participantId,
  source,
  muted,
}: {
  endpoint: CallEndpoint;
  participantId: string;
  source: 'camera' | 'screen';
  muted?: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    try {
      return endpoint.attach(participantId, source, ref.current);
    } catch {
      return undefined;
    }
  }, [endpoint, participantId, source]);

  return (
    <video ref={ref} autoPlay playsInline muted={muted} className="w-full h-full object-cover" />
  );
}
